import { auth } from "../utils/init-firebase";
import { getFirebaseAuthorizationHeaders } from "../utils/firebase-auth-headers";
import { JUNGLE_CLOUDFLARE_BASE_URL } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { parseInterfaceLanguageAuthority, type InterfaceLanguageAuthority } from "../../i18n/interfaceLanguagePreference";
import type { LanguagePreference, LocaleCode } from "../../i18n/localeRegistry";

export interface SaveInterfaceLanguageInput {
  preference?: LanguagePreference;
  effectiveLocale: LocaleCode;
  expectedRevision: string | null;
  source: "web" | "ios" | "android";
}
export interface InterfaceLanguageReply { language: InterfaceLanguageAuthority; conflict: boolean }

/** The owner is the current Firebase token, checked again after async token refresh. */
export async function requestInterfaceLanguage(accountId: string, input?: SaveInterfaceLanguageInput, signal?: AbortSignal): Promise<InterfaceLanguageReply> {
  const isOwner = () => auth.currentUser?.uid === accountId && !signal?.aborted;
  if (!isOwner()) throw new Error("Interface language account changed");
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal?.addEventListener("abort", cancel, { once: true });
  const timeout = setTimeout(cancel, 15000);
  let rejectCancellation: () => void;
  const cancelled = new Promise<never>((_resolve, reject) => {
    rejectCancellation = () => reject(new DOMException("Interface language request cancelled", "AbortError"));
    controller.signal.addEventListener("abort", rejectCancellation, { once: true });
  });
  try {
    const headers = await Promise.race([getFirebaseAuthorizationHeaders(), cancelled]);
    if (!isOwner() || controller.signal.aborted) throw new Error("Interface language account changed");
    const response = await Promise.race([fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/interface-language`, {
      method: input ? "PATCH" : "GET", headers: { ...headers, ...(input ? { "Content-Type": "application/json" } : {}) },
      ...(input ? { body: JSON.stringify(input) } : {}), signal: controller.signal, cache: "no-store",
    }), cancelled]);
    if (!isOwner() || controller.signal.aborted) throw new Error("Interface language account changed");
    if (!(response.ok || response.status === 409)) throw new Error("Interface language request failed");
    const result = await Promise.race([response.json(), cancelled]);
    const language = parseInterfaceLanguageAuthority(result.language);
    const stillOwnsResponse = isOwner() && !controller.signal.aborted;
    const hasExpectedStatus = result.status === (response.status === 409 ? "conflict" : "success");
    if (!stillOwnsResponse || !language || !hasExpectedStatus) throw new Error("Invalid interface language response");
    return { language, conflict: response.status === 409 };
  } finally {
    clearTimeout(timeout);
    controller.signal.removeEventListener("abort", rejectCancellation);
    signal?.removeEventListener("abort", cancel);
  }
}
